import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import {
  ASSEMBLY_DETAIL_COLUMNS,
  ASSEMBLY_PRIMARY_COLUMNS,
  formatAssemblyDate,
  formatAssemblyDateForInput,
  getAssemblyDueCompletionValue,
  getPackingListStatusClass,
  getProjectStatusClass,
  normalizeText,
} from './assemblyScheduleConfig';
import AssemblyMultiSelect from './AssemblyMultiSelect';
import QAFeedback from '../JobSchedulePage/QAFeedback';

const IDENTITY_FIELDS = new Set(['JobNumber', 'ModelNumber', 'ProjectName']);
const GENERAL_JOB_FIELDS = [
  { key: 'SalesRep', label: 'Sales Rep', source: 'job', editable: false },
  { key: 'ProjectManager', label: 'Project Manager', source: 'job', editable: false },
  { key: 'MechanicalEngineer', label: 'Mechanical Engineer', source: 'job', editable: false },
  { key: 'ElectricalEngineer', label: 'Electrical Engineer', source: 'job', editable: false },
];
const WORKFLOW_FIELDS = new Set([
  'AssemblyProjectStatus',
  'PackingListType',
  'PackingListStatus',
  'PackingListOwner',
  'Assembler',
  'AssemblyType',
  'ControlBoxTransferDate',
]);

const SHIPPING_FIELDS = new Set([
  'CurrentPlanShipDate',
  'AssemblyExpectedCompletion',
  'JobLocation',
  'Notes',
  'PaymentTerms',
]);
const MODAL_ONLY_SHIPPING_FIELDS = [
  {
    key: 'OriginalScheduledShipDate',
    label: 'Original Scheduled Ship Date',
    source: 'job',
    editable: false,
    type: 'date',
  },
];

const AssemblyEditModal = ({
  show,
  job,
  canEdit,
  dropdownOptionsByField,
  isSaving,
  error,
  onHide,
  onSave,
}) => {
  const [formData, setFormData] = useState({});
  const [showQAFeedback, setShowQAFeedback] = useState(false);

  useEffect(() => {
    if (!job) {
      setFormData({});
      return;
    }

    const initialValues = {};
    [
      ...ASSEMBLY_PRIMARY_COLUMNS,
      ...ASSEMBLY_DETAIL_COLUMNS,
      ...GENERAL_JOB_FIELDS,
      ...MODAL_ONLY_SHIPPING_FIELDS,
    ].forEach((field) => {
      initialValues[field.key] = field.type === 'date'
        ? formatAssemblyDateForInput(job[field.key])
        : (job[field.key] ?? '');
    });
    setFormData(initialValues);
  }, [job]);

  const sections = useMemo(() => [
    {
      key: 'identity',
      title: 'General Job Information',
      fields: [
        ...ASSEMBLY_PRIMARY_COLUMNS.filter((field) => IDENTITY_FIELDS.has(field.key)),
        ...GENERAL_JOB_FIELDS,
      ],
    },
    {
      key: 'workflow',
      title: 'Assembly Workflow',
      fields: ASSEMBLY_PRIMARY_COLUMNS.filter((field) => WORKFLOW_FIELDS.has(field.key)),
    },
    {
      key: 'timeline',
      title: 'Production Timeline',
      fields: ASSEMBLY_DETAIL_COLUMNS,
    },
    {
      key: 'shipping',
      title: 'Shipping and Notes',
      fields: [
        ...MODAL_ONLY_SHIPPING_FIELDS,
        ...ASSEMBLY_PRIMARY_COLUMNS.filter((field) => SHIPPING_FIELDS.has(field.key)),
      ],
    },
  ], []);

  const handleChange = (field, nextValue) => {
    setFormData((current) => ({ ...current, [field]: nextValue }));
  };

  const renderField = (field) => {
    const isEditable = Boolean(canEdit && field.editable);
    const options = dropdownOptionsByField[field.key] || [];
    const currentValue = formData[field.key] ?? '';
    const isAssemblyStatus = field.key === 'AssemblyProjectStatus';
    const isPackingListStatus = field.key === 'PackingListStatus';
    const getOptionStatusClass = (value) => {
      if (isAssemblyStatus) return getProjectStatusClass(value);
      if (isPackingListStatus) return getPackingListStatusClass(value);
      return '';
    };
    const hasCurrentOption = options.some(
      (option) => normalizeText(option).toLowerCase() === normalizeText(currentValue).toLowerCase()
    );

    if (field.type === 'dropdown' || field.type === 'user-dropdown' || field.type === 'sales-dropdown') {
      return (
        <Form.Select
          value={currentValue}
          onChange={(event) => handleChange(field.key, event.target.value)}
          disabled={!isEditable}
          className={getOptionStatusClass(currentValue)}
        >
          <option value="">Select...</option>
          {options.map((option) => (
            <option
              key={option}
              value={option}
              className={getOptionStatusClass(option)}
            >
              {option}
            </option>
          ))}
          {!hasCurrentOption && currentValue && (
            <option
              value={currentValue}
              className={getOptionStatusClass(currentValue)}
            >
              {currentValue}
            </option>
          )}
        </Form.Select>
      );
    }

    if (field.type === 'multi-dropdown') {
      return (
        <AssemblyMultiSelect
          value={currentValue}
          options={options}
          onChange={(nextValue) => handleChange(field.key, nextValue)}
          disabled={!isEditable}
        />
      );
    }

    if (field.type === 'textarea') {
      return (
        <Form.Control
          as="textarea"
          rows={3}
          value={currentValue}
          onChange={(event) => handleChange(field.key, event.target.value)}
          readOnly={!isEditable}
          disabled={!isEditable}
        />
      );
    }

    const input = (
      <Form.Control
        type={field.type === 'date' ? 'date' : 'text'}
        value={currentValue}
        onChange={(event) => handleChange(field.key, event.target.value)}
        readOnly={!isEditable}
        disabled={!isEditable}
      />
    );

    const completionValue = field.type === 'date'
      ? getAssemblyDueCompletionValue(job, field.key)
      : null;
    if (!completionValue) return input;

    return (
      <div className="assembly-modal-date-value">
        {input}
        <i
          className="bi bi-check-square-fill assembly-complete-check"
          title={`Completed ${formatAssemblyDate(completionValue) || normalizeText(completionValue)}`}
          aria-label="Completed"
        />
      </div>
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await onSave(formData);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      scrollable
      dialogClassName="assembly-edit-modal"
      contentClassName="assembly-edit-modal-content"
    >
      <Form onSubmit={handleSubmit} className="assembly-edit-modal-form">
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Assembly Schedule{job?.JobNumber ? ` — ${job.JobNumber}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {!canEdit && (
            <Alert variant="info">This assembly schedule is read-only for your current permissions.</Alert>
          )}

          {sections.map((section) => (
            <section className="assembly-modal-section" key={section.key}>
              <h5>{section.title}</h5>
              <Row className="g-3">
                {section.fields.map((field) => (
                  <Col
                    key={field.key}
                    xs={12}
                    md={field.type === 'textarea' ? 12 : 6}
                    lg={field.type === 'textarea' ? 12 : 4}
                  >
                    <Form.Group controlId={`assembly-modal-${field.key}`}>
                      <Form.Label>{field.label}</Form.Label>
                      {renderField(field)}
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </section>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="warning"
            onClick={() => setShowQAFeedback(true)}
            className="me-auto"
            disabled={!job}
          >
            <i className="bi bi-clipboard-check me-2" />
            Submit QA Feedback
          </Button>
          <Button variant="secondary" onClick={onHide} disabled={isSaving}>Close</Button>
          {canEdit && (
            <Button type="submit" variant="primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </Modal.Footer>
      </Form>
      <QAFeedback
        show={showQAFeedback}
        onHide={() => setShowQAFeedback(false)}
        job={job}
        jobId={job?.ID}
      />
    </Modal>
  );
};

export default AssemblyEditModal;
